import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import i18next from 'i18next';
import { I18nextProvider, useTranslation } from 'react-i18next';
import mainI18n from '../../i18n';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon,
  Draw as DrawIcon,
} from '@mui/icons-material';
import { createApiUrl } from '../../config/api';
import PdfDocumentView from './esign/PdfDocumentView';
import FieldOverlay from './esign/FieldOverlay';
import AdoptSignatureDialog from './esign/AdoptSignatureDialog';

const STATUS_MESSAGES = {
  expired: 'esign.sign.expired',
  voided: 'esign.sign.voided',
  declined: 'esign.sign.declined',
};

const isSignType = (type) => type === 'signature' || type === 'initials';
const apiUrl = (token, path = '') => createApiUrl(`/api/public/esign/${encodeURIComponent(token)}${path}`);

async function postJson(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || 'Request failed');
    err.code = data.code;
    err.fields = data.fields;
    throw err;
  }
  return data;
}

const Shell = ({ children, footerId }) => {
  const { t } = useTranslation();
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#eef1f5' }}>
      <AppBar position="static" elevation={0} sx={{ bgcolor: '#1c3366' }}>
        <Toolbar variant="dense">
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{t('esign.sign.brand')}</Typography>
        </Toolbar>
      </AppBar>
      {children}
      {footerId && (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ textAlign: 'center', py: 2, px: 2 }}>
          {t('esign.sign.footer', { id: footerId })}
        </Typography>
      )}
    </Box>
  );
};

const SigningPage = ({ token }) => {
  const { t, i18n } = useTranslation();
  const [session, setSession] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [pdfData, setPdfData] = useState(null);
  const [pdfError, setPdfError] = useState(false);
  const [agree, setAgree] = useState(false);
  const [consenting, setConsenting] = useState(false);
  const [values, setValues] = useState({});
  const [adopted, setAdopted] = useState(null);
  const [adoptOpen, setAdoptOpen] = useState(false);
  const [pendingFieldId, setPendingFieldId] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const [textField, setTextField] = useState(null);
  const [textValue, setTextValue] = useState('');
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const [done, setDone] = useState(null); // 'completed' | 'declined'
  const pageRefs = useRef({});

  const loadSession = useCallback(async () => {
    try {
      const res = await fetch(apiUrl(token));
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setLoadError(data.code === 'invalid_link' ? 'esign.sign.invalidLink' : 'esign.sign.error');
        return;
      }
      await i18n.changeLanguage(data.language === 'es-mx' ? 'es-MX' : 'en');
      setSession(data);
      const initial = {};
      data.fields.forEach((f) => {
        if (f.type === 'checkbox') initial[f.id] = f.defaultValue === 'true' ? 'true' : '';
      });
      setValues(initial);
    } catch {
      setLoadError('esign.sign.error');
    }
  }, [token, i18n]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const active = session && ['sent', 'viewed'].includes(session.status) && !done;
  const consented = session?.recipient?.consented;

  // Fetch the PDF once the signer is past the disclosure.
  useEffect(() => {
    if (!active || !consented || pdfData) return;
    (async () => {
      try {
        const res = await fetch(apiUrl(token, '/document'));
        if (!res.ok) throw new Error('document');
        setPdfData(await res.arrayBuffer());
      } catch {
        setPdfError(true);
      }
    })();
  }, [active, consented, pdfData, token]);

  const fields = useMemo(() => session?.fields || [], [session]);
  const needsInitials = fields.some((f) => f.type === 'initials');
  const requiredFields = fields.filter((f) => f.required && isSignType(f.type));
  const doneCount = requiredFields.filter((f) => values[f.id]).length;
  const nextField = requiredFields.find((f) => !values[f.id]);

  const scrollToField = (field) => {
    const el = document.getElementById(`esign-field-${field.id}`) || pageRefs.current[field.page];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightId(field.id);
  };

  const handleConsent = async () => {
    setConsenting(true);
    setActionError('');
    try {
      await postJson(apiUrl(token, '/consent'));
      setSession((s) => ({ ...s, recipient: { ...s.recipient, consented: true } }));
    } catch (e) {
      setActionError(e.message || t('esign.sign.error'));
    } finally {
      setConsenting(false);
    }
  };

  const handleActivate = (field) => {
    setActionError('');
    if (isSignType(field.type)) {
      if (values[field.id]) {
        setValues((v) => ({ ...v, [field.id]: false }));
        return;
      }
      if (!adopted) {
        setPendingFieldId(field.id);
        setAdoptOpen(true);
        return;
      }
      setValues((v) => ({ ...v, [field.id]: true }));
      setHighlightId(null);
      return;
    }
    if (field.type === 'checkbox') {
      setValues((v) => ({ ...v, [field.id]: v[field.id] === 'true' ? '' : 'true' }));
      return;
    }
    setTextField(field);
    setTextValue(values[field.id] || '');
  };

  const handleAdopt = (marks) => {
    setAdopted(marks);
    setAdoptOpen(false);
    if (pendingFieldId) {
      setValues((v) => ({ ...v, [pendingFieldId]: true }));
      setPendingFieldId(null);
      setHighlightId(null);
    }
  };

  const handleNext = () => {
    if (nextField) scrollToField(nextField);
  };

  const handleFinish = async () => {
    if (nextField) {
      setActionError(t('esign.sign.requiredMissing'));
      scrollToField(nextField);
      return;
    }
    setBusy(true);
    setActionError('');
    try {
      const fieldValues = {};
      fields.forEach((f) => {
        if (isSignType(f.type)) {
          if (values[f.id]) fieldValues[f.id] = 'signed';
        } else if (values[f.id] !== undefined) {
          fieldValues[f.id] = values[f.id];
        }
      });
      await postJson(apiUrl(token, '/complete'), { ...adopted, fieldValues });
      setDone('completed');
      window.scrollTo({ top: 0 });
    } catch (e) {
      setActionError(e.message || t('esign.sign.error'));
      if (e.fields?.length) {
        const missing = fields.find((f) => f.id === e.fields[0]);
        if (missing) scrollToField(missing);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDecline = async () => {
    setBusy(true);
    setActionError('');
    try {
      await postJson(apiUrl(token, '/decline'), { reason: declineReason });
      setDeclineOpen(false);
      setDone('declined');
      window.scrollTo({ top: 0 });
    } catch (e) {
      setActionError(e.message || t('esign.sign.error'));
      setDeclineOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const renderOverlay = useCallback((pageNumber, page) => fields
    .filter((f) => f.page === pageNumber)
    .map((f) => (
      <FieldOverlay
        key={f.id}
        field={f}
        page={page}
        value={values[f.id]}
        adopted={adopted}
        highlighted={highlightId === f.id}
        onActivate={handleActivate}
      />
    )),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [fields, values, adopted, highlightId]);

  const handlePdfError = useCallback(() => setPdfError(true), []);

  if (loadError) {
    return (
      <Shell>
        <Container maxWidth="sm" sx={{ py: 6 }}>
          <Alert severity="warning">{t(loadError)}</Alert>
        </Container>
      </Shell>
    );
  }

  if (!session) {
    return (
      <Shell>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
          <CircularProgress />
          <Typography color="text.secondary">{t('esign.sign.loading')}</Typography>
        </Box>
      </Shell>
    );
  }

  // Finished, declined, voided or expired.
  if (!active) {
    const completed = done === 'completed' || session.status === 'completed';
    const declined = done === 'declined' || session.status === 'declined';
    return (
      <Shell footerId={session.envelopeId}>
        <Container maxWidth="sm" sx={{ py: 6 }}>
          <Paper sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
            {completed ? (
              <>
                <CheckCircleIcon color="success" sx={{ fontSize: 56, mb: 1 }} />
                <Typography variant="h5" gutterBottom>{t('esign.sign.completeTitle')}</Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  {t('esign.sign.completeBody', { email: session.recipient.email })}
                </Typography>
                {(done === 'completed' || session.signedCopyAvailable) && (
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    href={`${apiUrl(token, '/document')}?download=1`}
                  >
                    {t('esign.sign.download')}
                  </Button>
                )}
              </>
            ) : (
              <Alert severity={declined ? 'info' : 'warning'} sx={{ textAlign: 'left' }}>
                {t(declined ? 'esign.sign.declined' : (STATUS_MESSAGES[session.status] || 'esign.sign.unavailable'))}
              </Alert>
            )}
          </Paper>
        </Container>
      </Shell>
    );
  }

  // Disclosure and consent come before the document.
  if (!consented) {
    return (
      <Shell footerId={session.envelopeId}>
        <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 5 } }}>
          <Paper sx={{ p: { xs: 2.5, sm: 4 } }}>
            <Typography variant="body1" gutterBottom>{t('esign.sign.welcome', { name: session.recipient.name })}</Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>{t('esign.sign.intro', { title: session.title })}</Typography>
            {session.message && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#fafafa' }}>
                <Typography variant="caption" color="text.secondary">{t('esign.sign.messageFrom')}</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{session.message}</Typography>
              </Paper>
            )}
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>{t('esign.sign.disclosureTitle')}</Typography>
            {['disclosure1', 'disclosure2', 'disclosure3', 'disclosure4'].map((key) => (
              <Typography key={key} variant="body2" color="text.secondary" paragraph>{t(`esign.sign.${key}`)}</Typography>
            ))}
            <FormControlLabel
              control={<Checkbox checked={agree} onChange={(e) => setAgree(e.target.checked)} />}
              label={t('esign.sign.agree')}
            />
            {actionError && <Alert severity="error" sx={{ mt: 2 }}>{actionError}</Alert>}
            <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ mt: 3 }}>
              <Button color="inherit" onClick={() => setDeclineOpen(true)}>{t('esign.sign.decline')}</Button>
              <Button variant="contained" onClick={handleConsent} disabled={!agree || consenting}>
                {t('esign.sign.continue')}
              </Button>
            </Stack>
          </Paper>
        </Container>
        {renderDeclineDialog()}
      </Shell>
    );
  }

  function renderDeclineDialog() {
    return (
      <Dialog open={declineOpen} onClose={() => setDeclineOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t('esign.sign.declineTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>{t('esign.sign.declineBody')}</Typography>
          <TextField
            label={t('esign.sign.declineReason')}
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            multiline
            minRows={3}
            fullWidth
            autoFocus
            inputProps={{ maxLength: 2000 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeclineOpen(false)}>{t('esign.sign.cancel')}</Button>
          <Button color="error" variant="contained" onClick={handleDecline} disabled={busy || !declineReason.trim()}>
            {t('esign.sign.declineConfirm')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Shell footerId={session.envelopeId}>
      {/* Sticky action bar */}
      <Paper
        square
        elevation={3}
        sx={{ position: 'sticky', top: 0, zIndex: 10, px: { xs: 1.5, sm: 3 }, py: 1 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap>{session.title}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t('esign.sign.progress', { done: doneCount, total: requiredFields.length })}
            </Typography>
          </Box>
          <Button size="small" color="inherit" onClick={() => setDeclineOpen(true)} sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>
            {t('esign.sign.decline')}
          </Button>
          {adopted && (
            <Button size="small" startIcon={<DrawIcon />} onClick={() => setAdoptOpen(true)} sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
              {t('esign.sign.changeSignature')}
            </Button>
          )}
          {nextField ? (
            <Button variant="contained" color="warning" onClick={handleNext}>
              {doneCount === 0 ? t('esign.sign.start') : t('esign.sign.next')}
            </Button>
          ) : (
            <Button variant="contained" color="success" onClick={handleFinish} disabled={busy}>
              {busy ? t('esign.sign.finishing') : t('esign.sign.finish')}
            </Button>
          )}
        </Stack>
        {actionError && <Alert severity="error" sx={{ mt: 1 }} onClose={() => setActionError('')}>{actionError}</Alert>}
      </Paper>

      <Container maxWidth="md" sx={{ py: 2, px: { xs: 1, sm: 2 } }}>
        {pdfError ? (
          <Alert severity="error">{t('esign.sign.pdfError')}</Alert>
        ) : (
          <PdfDocumentView data={pdfData} renderOverlay={renderOverlay} onError={handlePdfError} pageRefs={pageRefs} />
        )}
        <Stack direction="row" justifyContent="space-between" sx={{ mt: 2, mb: 4 }}>
          <Button color="inherit" onClick={() => setDeclineOpen(true)}>{t('esign.sign.decline')}</Button>
          {nextField ? (
            <Button variant="contained" color="warning" onClick={handleNext}>{t('esign.sign.next')}</Button>
          ) : (
            <Button variant="contained" color="success" onClick={handleFinish} disabled={busy}>
              {busy ? t('esign.sign.finishing') : t('esign.sign.finish')}
            </Button>
          )}
        </Stack>
      </Container>

      <AdoptSignatureDialog
        open={adoptOpen}
        defaultName={adopted?.adoptedName || session.recipient.name}
        needsInitials={needsInitials}
        onAdopt={handleAdopt}
        onClose={() => {
          setAdoptOpen(false);
          setPendingFieldId(null);
        }}
      />

      <Dialog open={!!textField} onClose={() => setTextField(null)} fullWidth maxWidth="xs">
        <DialogTitle>
          {textField && t(`esign.sign.fieldLabels.${String(textField.anchor || '').replace(/\//g, '')}`, { defaultValue: textField.label })}
        </DialogTitle>
        <DialogContent>
          <TextField
            value={textValue}
            onChange={(e) => setTextValue(e.target.value)}
            type={textField?.type === 'email' ? 'email' : textField?.type === 'number' ? 'tel' : 'text'}
            fullWidth
            autoFocus
            margin="dense"
            inputProps={{ maxLength: textField?.type === 'number' ? 40 : 200 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTextField(null)}>{t('esign.sign.cancel')}</Button>
          <Button
            variant="contained"
            onClick={() => {
              setValues((v) => ({ ...v, [textField.id]: textValue.trim() }));
              setTextField(null);
            }}
          >
            {t('esign.sign.ok')}
          </Button>
        </DialogActions>
      </Dialog>

      {renderDeclineDialog()}
    </Shell>
  );
};

/**
 * Public signing page (/sign/:token). It runs on its own i18n instance so the
 * envelope's language never changes the language QERB staff have chosen in
 * the same browser.
 */
const SignDocumentPage = () => {
  const { token } = useParams();
  const signI18n = useMemo(() => {
    const instance = i18next.createInstance();
    instance.init({
      resources: mainI18n.options.resources,
      lng: 'en',
      fallbackLng: 'en',
      interpolation: { escapeValue: false },
    });
    return instance;
  }, []);
  return (
    <I18nextProvider i18n={signI18n}>
      <SigningPage token={token} />
    </I18nextProvider>
  );
};

export default SignDocumentPage;
